import { CroNetwork } from '@crypto-org-chain/chain-jslib/lib/dist/core/cro';
import { Bech32Address } from "@keplr-wallet/cosmos";
import { ChainInfo } from "@keplr-wallet/types";
import { AxiosRequestConfig } from "axios";
import protobuf from 'protobufjs';
import { cosmos, google, chainmain } from '@crypto-org-chain/chain-jslib/lib/dist/cosmos/v1beta1/codec';
import { getRandomId } from '../crypto/RandomGen';

export const APP_DB_NAMESPACE = 'data-store';
export const MARKET_API_BASE_URL = 'https://crypto.org/api';
export const NV_GRAPHQL_API_ENDPOINT = 'https://crypto.com/nft-api/graphql';
export const IPFS_MIDDLEWARE_SERVER_UPLOAD_ENDPOINT =
  'https://crypto.org/ipfs-middleware-server/uploads';
export const DEFAULT_CLIENT_MEMO = 'client:chain-desktop-app';

export const NodePorts = {
  Tendermint: ':26657',
  Cosmos: ':1317',
};
// maximum in ledger: 2147483647
export const LedgerWalletMaximum = 2147483647;

export type WalletConfig = {
  enabled: boolean;
  name: string;
  explorerUrl: string;
  nodeUrl: string;
  indexingUrl: string;
  derivationPath: string;
  network: Network;
  disableDefaultClientMemo: boolean;
  // When enabled all settings update will be propagated to all wallets of the same network.
  // E.g: User updates nodeURL in one mainnet wallet, all other mainnet wallets will have the new nodeURL
  enableGeneralSettings: boolean;
  analyticsDisabled: boolean;
  fee: {
    gasLimit: string;
    networkFee: string;
  };
};

export const FIXED_DEFAULT_FEE = String(10_000);
export const FIXED_DEFAULT_GAS_LIMIT = String(300_000);

export const NFT_IMAGE_DENOM_SCHEMA = {
  title: 'Asset Metadata',
  type: 'Object',
  properties: {
    description: {
      type: 'string',
      description: 'Describes the asset to which this NFT represents',
    },
    name: {
      type: 'string',
      description: 'Identifies the asset to which this NFT represents',
    },
    image: {
      type: 'string',
      description: 'A URI pointing to a resource with mime type image',
    },
    mimeType: {
      type: 'string',
      description: 'Describes the type of represented NFT media',
    },
  },
};
export const NFT_VIDEO_DENOM_SCHEMA = {
  title: 'Asset Metadata',
  type: 'Object',
  properties: {
    description: {
      type: 'string',
      description: 'Describes the asset to which this NFT represents',
    },
    name: {
      type: 'string',
      description: 'Identifies the asset to which this NFT represents',
    },
    image: {
      type: 'string',
      description: 'A URI pointing to a resource with mime type image',
    },
    animation_url: {
      type: 'string',
      description: 'A URI pointing to a resource with mime type video',
    },
    mimeType: {
      type: 'string',
      description: 'Describes the type of represented NFT media',
    },
  },
};

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
export const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

const TestNetConfig: WalletConfig = {
  enabled: true,
  name: 'TESTNET',
  derivationPath: "m/44'/1'/0'/0/0",
  explorerUrl: 'https://crypto.org/explorer/croeseid',
  indexingUrl: 'https://crypto.org/explorer/croeseid/api/v1/',
  nodeUrl: CroNetwork.Testnet.defaultNodeUrl,
  network: CroNetwork.Testnet,
  disableDefaultClientMemo: false,
  enableGeneralSettings: false,
  analyticsDisabled: false,
  fee: {
    gasLimit: FIXED_DEFAULT_GAS_LIMIT,
    networkFee: FIXED_DEFAULT_FEE,
  },
};

const TestNetCroeseid3: WalletConfig = {
  enabled: true,
  name: 'TESTNET CROESEID 3',
  derivationPath: "m/44'/1'/0'/0/0",
  explorerUrl: 'https://crypto.org/explorer/croeseid3',
  indexingUrl: 'https://crypto.org/explorer/croeseid3/api/v1/',
  nodeUrl: CroNetwork.TestnetCroeseid3.defaultNodeUrl,
  network: CroNetwork.TestnetCroeseid3,
  disableDefaultClientMemo: false,
  enableGeneralSettings: false,
  analyticsDisabled: false,
  fee: {
    gasLimit: FIXED_DEFAULT_GAS_LIMIT,
    networkFee: FIXED_DEFAULT_FEE,
  },
};

// This constant value is used when actual values are not known yet
// For instance :
export const NOT_KNOWN_YET_VALUE = 'TO_BE_DECIDED';

const MainNetConfig: WalletConfig = {
  enabled: true,
  name: 'MAINNET',
  derivationPath: "m/44'/394'/0'/0/0",
  nodeUrl: CroNetwork.Mainnet.defaultNodeUrl,
  explorerUrl: 'https://crypto.org/explorer',
  indexingUrl: 'https://crypto.org/explorer/api/v1/',
  network: CroNetwork.Mainnet,
  disableDefaultClientMemo: false,
  enableGeneralSettings: false,
  analyticsDisabled: false,
  fee: {
    gasLimit: FIXED_DEFAULT_GAS_LIMIT,
    networkFee: FIXED_DEFAULT_FEE,
  },
};

// Supposed to be fully customizable by the user when it will be supported
export const CustomDevNet: WalletConfig = {
  derivationPath: "m/44'/394'/0'/0/0",
  enabled: true,
  name: 'CUSTOM DEVNET',
  disableDefaultClientMemo: false,
  enableGeneralSettings: false,
  analyticsDisabled: false,
  network: {
    defaultNodeUrl: '',
    chainId: 'test',
    addressPrefix: 'cro',
    bip44Path: { coinType: 394, account: 0 },
    validatorPubKeyPrefix: 'crocnclpub',
    validatorAddressPrefix: 'crocncl',
    coin: { baseDenom: 'basecro', croDenom: 'cro' },
  },
  nodeUrl: '',
  indexingUrl: '',
  explorerUrl: '',
  fee: {
    gasLimit: FIXED_DEFAULT_GAS_LIMIT,
    networkFee: FIXED_DEFAULT_FEE,
  },
};

// Available wallet configs will be presented to the user on wallet creation
// The user can either select default configs available or simply configure their own configuration from scratch
export const DefaultWalletConfigs = {
  TestNetConfig,
  MainNetConfig,
  CustomDevNet,
  TestNetCroeseid3,
};

// Every created wallet get initialized with a new CRO asset
export const DefaultAsset = (network: Network) => {
  const assetSymbol = network.coin.croDenom.toString().toUpperCase();
  return {
    balance: '0',
    description:
      'Crypto.org Coin (CRO) is the native token of the Crypto.org Chain. The Crypto.org Chain was created to build a network of cryptocurrency projects, and develop merchants’ ability to accept crypto as a form of payment. The Crypto.org Chain is a high performing native blockchain solution, which will make the transaction flows between crypto users and merchants accepting crypto seamless, cost-efficient and secure.\\r\\n\\r\\nBusinesses can use Crypto.org pay Checkout and/or Invoice to enable customers to complete checkout and pay for goods and services with cryptocurrencies using the Crypto.org Wallet App. Businesses receive all their payments instantly in CRO or stable coins, or in fiat.',
    icon_url:
      'https://s3-ap-southeast-1.amazonaws.com/monaco-cointrack-production/uploads/coin/colorful_logo/5c1248c15568a4017c20aa87/cro.png',
    identifier: getRandomId(),
    name: 'Crypto.org Coin',
    symbol: assetSymbol,
    mainnetSymbol: 'CRO', // This is to be used solely for markets data since testnet market prices is always non existent
    stakedBalance: '0',
    decimals: 8,
  };
};

// This type is a copy of the Network type defined inside chain-js
// The redefinition is a work-around on limitation to lib to export it
export type Network = {
  defaultNodeUrl: string;
  chainId: string;
  addressPrefix: string;
  bip44Path: { coinType: number; account: number };
  validatorPubKeyPrefix: string;
  validatorAddressPrefix: string;
  coin: { baseDenom: string; croDenom: string };
  rpcUrl?: string;
};

const CRYPTO_ORG_RPC_ENDPOINT = "https://testnet-croeseid-3.crypto.org:26657";
const CRYPTO_ORG_RPC_CONFIG: AxiosRequestConfig | undefined = undefined;
const CRYPTO_ORG_REST_ENDPOINT = "https://testnet-croeseid-3.crypto.org:1317";
const CRYPTO_ORG_REST_CONFIG: AxiosRequestConfig | undefined = undefined;

export const chainInfoCroeseid3: ChainInfo =
{
  rpc: CRYPTO_ORG_RPC_ENDPOINT,
  rpcConfig: CRYPTO_ORG_RPC_CONFIG,
  rest: CRYPTO_ORG_REST_ENDPOINT,
  restConfig: CRYPTO_ORG_REST_CONFIG,
  chainId: "testnet-croeseid-3",
  chainName: "Crypto.org Testnet-Croeseid-3",
  stakeCurrency: {
    coinDenom: "tcro",
    coinMinimalDenom: "basetcro",
    coinDecimals: 8,
    coinGeckoId: "crypto-com-chain",
  },
  walletUrl:
    process.env.NODE_ENV === "production"
      ? "https://wallet.keplr.app/#/crypto-org/stake"
      : "http://localhost:8081/#/crypto-org/stake",
  walletUrlForStaking:
    process.env.NODE_ENV === "production"
      ? "https://wallet.keplr.app/#/crypto-org/stake"
      : "http://localhost:8081/#/crypto-org/stake",
  bip44: {
    // coinType: 394,
    coinType: 1,
  },
  bech32Config: Bech32Address.defaultBech32Config("tcro"),
  currencies: [
    {
      coinDenom: "tcro",
      coinMinimalDenom: "basetcro",
      coinDecimals: 8,
      coinGeckoId: "crypto-com-chain",
    },
  ],
  feeCurrencies: [
    {
      coinDenom: "tcro",
      coinMinimalDenom: "basetcro",
      coinDecimals: 8,
      coinGeckoId: "crypto-com-chain",
    },
  ],
  gasPriceStep: {
    low: 0.025,
    average: 0.03,
    high: 0.04,
  },
  features: ["stargate", "ibc-transfer"],
};

export interface GeneratedType {
    readonly create: (properties?: {
        [k: string]: any;
    }) => any;
    readonly encode: (message: any | {
        [k: string]: any;
    }, writer?: protobuf.Writer) => protobuf.Writer;
    readonly decode: (reader: protobuf.Reader | Uint8Array, length?: number) => any;
}

export const typeUrlMappings: {
    [key: string]: GeneratedType;
} = {
    '/cosmos.base.v1beta1.Coin': cosmos.base.v1beta1.Coin,
    '/cosmos.bank.v1beta1.MsgSend': cosmos.bank.v1beta1.MsgSend,
    '/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward': cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward,
    '/cosmos.staking.v1beta1.MsgCreateValidator': cosmos.staking.v1beta1.MsgCreateValidator,
    '/cosmos.staking.v1beta1.MsgBeginRedelegate': cosmos.staking.v1beta1.MsgBeginRedelegate,
    '/cosmos.staking.v1beta1.MsgEditValidator': cosmos.staking.v1beta1.MsgEditValidator,
    '/cosmos.staking.v1beta1.MsgDelegate': cosmos.staking.v1beta1.MsgDelegate,
    '/cosmos.staking.v1beta1.MsgUndelegate': cosmos.staking.v1beta1.MsgUndelegate,
    '/cosmos.distribution.v1beta1.MsgWithdrawValidatorCommission': cosmos.distribution.v1beta1.MsgWithdrawValidatorCommission,
    '/cosmos.crypto.ed25519.PubKey': cosmos.crypto.ed25519.PubKey,
    '/cosmos.crypto.secp256k1.PubKey': cosmos.crypto.secp256k1.PubKey,
    '/cosmos.gov.v1beta1.MsgDeposit': cosmos.gov.v1beta1.MsgDeposit,
    '/cosmos.gov.v1beta1.MsgVote': cosmos.gov.v1beta1.MsgVote,
    '/cosmos.gov.v1beta1.MsgSubmitProposal': cosmos.gov.v1beta1.MsgSubmitProposal,
    '/google.protobuf.Any': google.protobuf.Any,
    '/cosmos.distribution.v1beta1.MsgSetWithdrawAddress': cosmos.distribution.v1beta1.MsgSetWithdrawAddress,
    '/cosmos.distribution.v1beta1.MsgFundCommunityPool': cosmos.distribution.v1beta1.MsgFundCommunityPool,
    '/chainmain.nft.v1.MsgIssueDenom': chainmain.nft.v1.MsgIssueDenom,
    '/chainmain.nft.v1.MsgMintNFT': chainmain.nft.v1.MsgMintNFT,
    '/chainmain.nft.v1.MsgEditNFT': chainmain.nft.v1.MsgEditNFT,
    '/chainmain.nft.v1.MsgTransferNFT': chainmain.nft.v1.MsgTransferNFT,
    '/chainmain.nft.v1.MsgBurnNFT': chainmain.nft.v1.MsgBurnNFT,
};